import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Row, Col, Card, Select, Typography, Tag, Space, Button, Badge, Empty, Statistic,
  Switch, List, Avatar, Alert,
} from 'antd';
import {
  AlertOutlined, ReloadOutlined, WarningOutlined, ExclamationCircleOutlined,
  InfoCircleOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import { getEvents, getAnomalies } from '../api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const PRIMARY = '#0F1D6B';

const SEVERITY = {
  critical: { color: '#dc2626', bg: '#fee2e2', tag: 'red',    icon: <AlertOutlined />, label: 'CRITICAL' },
  warning:  { color: '#f59e0b', bg: '#fef3c7', tag: 'orange', icon: <WarningOutlined />, label: 'WARNING' },
  info:     { color: '#0284c7', bg: '#e0f2fe', tag: 'blue',   icon: <InfoCircleOutlined />, label: 'INFO' },
};

export default function AnomalyCenter() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    getEvents().then(r => {
      const ev = r.data.data || [];
      setEvents(ev);
      const a = ev.find(e => e.status === 'active') || ev[0];
      if (a) setEventId(a.id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!eventId) return;
    setLoading(true);
    getAnomalies({ event_id: eventId })
      .then(r => {
        setAnomalies(r.data.data || []);
        setSummary(r.data.summary || {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => {
    load();
    if (autoRefresh && eventId) intervalRef.current = setInterval(load, 15000);
    return () => clearInterval(intervalRef.current);
  }, [load, autoRefresh, eventId]);

  const filtered = filter === 'all' ? anomalies : anomalies.filter(a => a.severity === filter);

  return (
    <div>
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: PRIMARY }}>
            <AlertOutlined style={{ marginRight: 8, color: '#dc2626' }} />Anomaly Detection Center
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Automatic detection of unusual patterns: stuck tags, silent readers, capacity surges, long stays
          </Text>
        </Col>
        <Col>
          <Space>
            <Space size={6}><Text style={{ fontSize: 12 }}>Auto-refresh</Text>
              <Switch checked={autoRefresh} onChange={setAutoRefresh} size="small" /></Space>
            <Select value={eventId} onChange={setEventId} style={{ width: 220 }} placeholder="Event">
              {events.map(e => (
                <Option key={e.id} value={e.id}>
                  <Badge color={e.status === 'active' ? 'green' : 'default'} text={e.event_name} />
                </Option>
              ))}
            </Select>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          </Space>
        </Col>
      </Row>

      {/* Severity summary */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${PRIMARY}`, cursor: 'pointer' }}
            onClick={() => setFilter('all')}>
            <Statistic title={<Space>All Anomalies {filter === 'all' && <Tag>active</Tag>}</Space>}
              value={summary.total || 0} prefix={<AlertOutlined />} valueStyle={{ color: PRIMARY }} />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: '3px solid #dc2626', cursor: 'pointer' }}
            onClick={() => setFilter('critical')}>
            <Statistic title={<Space>Critical {filter === 'critical' && <Tag color="red">active</Tag>}</Space>}
              value={summary.critical || 0} valueStyle={{ color: '#dc2626' }} />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: '3px solid #f59e0b', cursor: 'pointer' }}
            onClick={() => setFilter('warning')}>
            <Statistic title={<Space>Warnings {filter === 'warning' && <Tag color="orange">active</Tag>}</Space>}
              value={summary.warning || 0} valueStyle={{ color: '#f59e0b' }} />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: '3px solid #0284c7', cursor: 'pointer' }}
            onClick={() => setFilter('info')}>
            <Statistic title={<Space>Info {filter === 'info' && <Tag color="blue">active</Tag>}</Space>}
              value={summary.info || 0} valueStyle={{ color: '#0284c7' }} />
          </Card>
        </Col>
      </Row>

      {anomalies.length === 0 && !loading ? (
        <Alert
          type="success"
          message="No anomalies detected"
          description="System health is nominal. All readers responding, no excessive scans, no stuck tags."
          showIcon
          style={{ borderRadius: 10 }}
        />
      ) : (
        <Card style={{ borderRadius: 10 }}>
          <List
            dataSource={filtered}
            locale={{ emptyText: <Empty description="No anomalies match this filter" /> }}
            renderItem={(a, i) => {
              const s = SEVERITY[a.severity] || SEVERITY.info;
              return (
                <List.Item key={i} style={{
                  padding: '14px 16px', background: i % 2 ? '#fafafa' : '#fff',
                  borderLeft: `4px solid ${s.color}`, marginBottom: 6, borderRadius: 8,
                }}>
                  <List.Item.Meta
                    avatar={
                      <Avatar size={42} style={{ background: s.bg, color: s.color, fontSize: 22 }}>
                        {a.icon || s.icon}
                      </Avatar>
                    }
                    title={
                      <Space>
                        <Text strong>{a.title}</Text>
                        <Tag color={s.tag} style={{ fontWeight: 700, letterSpacing: 1 }}>{s.label}</Tag>
                        {a.timestamp && (
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {dayjs(a.timestamp).fromNow()} · {dayjs(a.timestamp).format('HH:mm:ss')}
                          </Text>
                        )}
                      </Space>
                    }
                    description={
                      <Paragraph style={{ margin: 0, color: '#475569' }}>{a.detail}</Paragraph>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </Card>
      )}
    </div>
  );
}
